import { customZoneBounds, type CustomZoneEntryMode } from '../../utils/customZoneBounds';
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { displayInputDigits, normalizeInputDigits } from '../../utils/localizedInput';
import { NmButton } from '../NmButton';

export interface ZoneRow {
  label: string;
  minimum: number;
  maximum: number;
}

interface ZoneDraft {
  mode?: CustomZoneEntryMode;
  label: string;
  minimum: string;
  maximum: string;
}

/** Saved zones table with inline label editing. */
export function EditableZoneTable({
  zones,
  unit,
  formatRange,
  onSaveLabels,
  saving,
}: {
  zones: ZoneRow[];
  unit: string;
  /** Override default min–max unit display (e.g. pace /km for speed zones). */
  formatRange?: (zone: ZoneRow) => string;
  onSaveLabels?: (zones: ZoneRow[]) => Promise<void>;
  saving?: boolean;
}) {
  const { i18n } = useTranslation();
  const localize = (value: string) => displayInputDigits(value, i18n.language);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draftLabels, setDraftLabels] = useState<Record<number, string>>({});

  const hasLabelChanges = Object.keys(draftLabels).length > 0;

  function displayLabel(index: number, zone: ZoneRow) {
    return draftLabels[index] ?? zone.label;
  }

  async function saveLabels() {
    if (!onSaveLabels || !hasLabelChanges) return;
    const updated = zones.map((z, i) => (
      draftLabels[i] !== undefined ? { ...z, label: draftLabels[i] } : z
    ));
    await onSaveLabels(updated);
    setDraftLabels({});
    setEditingIndex(null);
  }

  if (zones.length === 0) return null;

  return (
    <div>
      <div style={styles.sectionTitle}>Saved zones</div>
      <div style={styles.zoneTable}>
        {zones.map((z, i) => (
          <div key={i} style={styles.zoneRow}>
            <div style={styles.zoneLabelCell}>
              {editingIndex === i ? (
                <input
                  type="text"
                  value={localize(displayLabel(i, z))}
                  onChange={(e) => setDraftLabels((prev) => ({ ...prev, [i]: e.target.value }))}
                  onBlur={() => setEditingIndex(null)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setEditingIndex(null); }}
                  autoFocus
                  style={styles.zoneLabelInput}
                />
              ) : (
                <button
                  type="button"
                  style={styles.zoneLabelBtn}
                  onClick={() => onSaveLabels && setEditingIndex(i)}
                  title={onSaveLabels ? 'Edit label' : undefined}
                >
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{localize(displayLabel(i, z))}</span>
                  {onSaveLabels && <span style={styles.editIcon}>✎</span>}
                </button>
              )}
            </div>
            <span dir="auto" style={{ fontSize: 12, color: 'var(--fg-3)', unicodeBidi: 'isolate', fontVariantNumeric: 'tabular-nums' }}>
              {localize(formatRange ? formatRange(z) : `${z.minimum} – ${z.maximum} ${unit}`)}
            </span>
          </div>
        ))}
      </div>
      {onSaveLabels && hasLabelChanges && (
        <NmButton
          variant="secondary"
          onClick={() => void saveLabels()}
          disabled={saving}
          style={{ marginTop: 10 }}
        >
          {saving ? 'Saving…' : 'Save zone names'}
        </NmButton>
      )}
    </div>
  );
}

/** Collapsible custom zone builder — mirrors mobile PowerZones custom form. */
export function CustomZoneBuilder({
  unit,
  thresholdFields,
  percentageThreshold,
  onSave,
}: {
  unit: string;
  thresholdFields: React.ReactNode;
  percentageThreshold?: { value: string; label: string };
  onSave: (zones: ZoneRow[]) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const localize = (value: string) => displayInputDigits(value, i18n.language);
  const [open, setOpen] = useState(false);
  const [customZones, setCustomZones] = useState<ZoneDraft[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [newMin, setNewMin] = useState('');
  const [newMax, setNewMax] = useState('');
  const [saving, setSaving] = useState(false);
  const unitId = useId();
  const [entryMode, setEntryMode] = useState<CustomZoneEntryMode>(percentageThreshold ? 'percent' : 'absolute');
  const bounds = (z: ZoneDraft) => customZoneBounds(z.minimum, z.maximum, z.mode ?? 'absolute', percentageThreshold?.value);
  const draftBounds = percentageThreshold ? bounds({ label: newLabel, minimum: newMin, maximum: newMax, mode: entryMode }) : null;

  function addZone() {
    if (!newLabel.trim()) { alert(t('athleteSettings.trainingEditor.nameRequired')); return; }
    if (!newMin || !newMax) { alert(t('athleteSettings.trainingEditor.boundsRequired')); return; }
    if (Number(newMin) >= Number(newMax)) { alert(t('athleteSettings.trainingEditor.badBounds')); return; }
    if (percentageThreshold && !bounds({ label: newLabel, minimum: newMin, maximum: newMax, mode: entryMode })) { alert(t('athleteSettings.trainingEditor.customInputInvalid')); return; }
    setCustomZones((prev) => [...prev, { label: newLabel.trim(), minimum: newMin, maximum: newMax, mode: entryMode }]);
    setNewLabel('');
    setNewMin('');
    setNewMax('');
  }

  function removeZone(index: number) {
    setCustomZones((prev) => prev.filter((_, i) => i !== index));
  }

  async function saveCustom() {
    if (customZones.length === 0) { alert(t('athleteSettings.trainingEditor.addFirst')); return; }
    if (percentageThreshold && customZones.some(z => !bounds(z))) { alert(t('athleteSettings.trainingEditor.customInputReview')); return; }
    setSaving(true);
    try {
      await onSave(customZones.map((z) => ({
        label: z.label,
        minimum: percentageThreshold ? bounds(z)!.minimum : Number(z.minimum),
        maximum: percentageThreshold ? bounds(z)!.maximum : Number(z.maximum),
      })));
      setCustomZones([]);
      setOpen(false);
    } catch (e: unknown) {
      alert(t('athleteSettings.trainingEditor.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.customSection}>
      <button
        type="button"
        style={styles.customToggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={styles.customToggleLabel}>{t('athleteSettings.powerEditor.custom')}</span>
        <span style={{ color: 'var(--fg-4)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="zone-custom-builder__body" style={styles.customBody}>
          {thresholdFields}

          {percentageThreshold && <div className="zone-custom-builder__mode">
            <span>{t('athleteSettings.trainingEditor.customInputMode')}</span>
            <NmButton variant={entryMode === 'absolute' ? 'primary' : 'secondary'} aria-pressed={entryMode === 'absolute'} onClick={() => setEntryMode('absolute')}>{t('athleteSettings.trainingEditor.customInputAbsolute')}</NmButton>
            <NmButton variant={entryMode === 'percent' ? 'primary' : 'secondary'} aria-pressed={entryMode === 'percent'} onClick={() => setEntryMode('percent')}>{t('athleteSettings.trainingEditor.customInputPercent')}</NmButton>
            <small>{t('athleteSettings.trainingEditor.customInputHintPrefix')}{percentageThreshold.label}{t('athleteSettings.trainingEditor.customInputHintSuffix')}</small>
          </div>}
          <div style={styles.sectionTitle}>{t('athleteSettings.powerEditor.addZone')}</div>
          <div className="sr-only" id={unitId}>{percentageThreshold && entryMode === 'percent' ? '٪' : unit}</div>
          <div className="zone-custom-builder__fields" style={styles.addRow}>
            <input
              type="text"
              placeholder={t('athleteSettings.powerEditor.zoneName')}
              aria-label={t('athleteSettings.powerEditor.zoneName')}
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              style={styles.input}
            />
            <label className="power-zone-editor__ftp zone-custom-builder__unit-input"><span dir="rtl" aria-hidden="true">{percentageThreshold && entryMode === 'percent' ? '٪' : unit}</span>            <input
              type={i18n.language.startsWith('fa') ? 'text' : 'number'}
              inputMode="decimal"
              placeholder={t('athleteSettings.powerEditor.min')}
              aria-describedby={unitId}
              aria-label={t('athleteSettings.powerEditor.min')}
              value={localize(newMin)}
              onChange={(e) => setNewMin(normalizeInputDigits(e.target.value))}
              className="zone-custom-builder__bound"
            /></label>
            <label className="power-zone-editor__ftp zone-custom-builder__unit-input"><span dir="rtl" aria-hidden="true">{percentageThreshold && entryMode === 'percent' ? '٪' : unit}</span>            <input
              type={i18n.language.startsWith('fa') ? 'text' : 'number'}
              inputMode="decimal"
              placeholder={t('athleteSettings.powerEditor.max')}
              aria-describedby={unitId}
              aria-label={t('athleteSettings.powerEditor.max')}
              value={localize(newMax)}
              onChange={(e) => setNewMax(normalizeInputDigits(e.target.value))}
              className="zone-custom-builder__bound"
            /></label>
            <NmButton variant="secondary" onClick={addZone} style={{ flexShrink: 0 }}>
              {t('athleteSettings.powerEditor.add')}
            </NmButton>
          </div>

          {percentageThreshold && entryMode === 'percent' && draftBounds && (
            <output className="zone-custom-builder__range" aria-live="polite" dir="ltr">
              <span dir="rtl">{unit}</span><bdi>{localize(String(draftBounds.minimum) + ' – ' + String(draftBounds.maximum))}</bdi>
            </output>
          )}

          {customZones.length > 0 && (
            <div style={{ ...styles.zoneTable, marginTop: 10 }}>
              {customZones.map((z, i) => (
                <div key={i} style={styles.zoneRow}>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{localize(z.label)}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span dir="ltr" className="zone-custom-builder__range"><span dir="rtl">{unit}</span> <bdi>{localize(percentageThreshold ? (bounds(z) ? String(bounds(z)!.minimum) + ' – ' + String(bounds(z)!.maximum) : '—') : z.minimum + ' – ' + z.maximum)}</bdi>{z.mode === 'percent' && <small>{localize(z.minimum + ' – ' + z.maximum)}٪</small>}</span>
                    <button type="button" style={styles.removeBtn} onClick={() => removeZone(i)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <NmButton
            variant="primary"
            onClick={() => void saveCustom()}
            disabled={saving || customZones.length === 0}
            style={{ marginTop: 12 }}
          >
            {t(saving ? 'athleteSettings.powerEditor.saving' : 'athleteSettings.powerEditor.saveCustom')}
          </NmButton>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--fg-4)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 8,
    marginTop: 4,
  },
  zoneTable: {
    borderRadius: 'var(--r-3)',
    border: '1px solid var(--surface-sunken)',
    overflow: 'hidden',
    boxShadow: 'var(--nm-inset-sm)',
  },
  zoneRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid var(--surface-sunken)',
    background: 'var(--surface-0)',
    gap: 8,
  },
  zoneLabelCell: { flex: 1, minWidth: 0 },
  zoneLabelBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    color: 'var(--fg-1)',
  },
  zoneLabelInput: {
    width: '100%',
    padding: '4px 8px',
    borderRadius: 'var(--r-2)',
    border: '1px solid var(--surface-sunken)',
    fontSize: 12,
    background: 'var(--surface-0)',
  },
  editIcon: { fontSize: 11, color: 'var(--fg-4)' },
  customSection: {
    marginTop: 20,
    borderRadius: 'var(--r-3)',
    border: '1px solid var(--surface-sunken)',
    overflow: 'hidden',
    boxShadow: 'var(--nm-raised-sm)',
  },
  customToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '12px 14px',
    border: 'none',
    background: 'var(--surface-sunken)',
    cursor: 'pointer',
    textAlign: 'left',
  },
  customToggleLabel: { fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' },
  customBody: { padding: '14px' },
  addRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  input: {
    flex: '1 1 120px',
    padding: '8px 10px',
    borderRadius: 'var(--r-2)',
    border: '1px solid var(--surface-sunken)',
    background: 'var(--surface-0)',
    fontSize: 13,
    color: 'var(--fg-1)',
    boxSizing: 'border-box',
  },
  inputSm: {
    flex: '0 1 72px',
    padding: '8px 10px',
    borderRadius: 'var(--r-2)',
    border: '1px solid var(--surface-sunken)',
    background: 'var(--surface-0)',
    fontSize: 13,
    color: 'var(--fg-1)',
    boxSizing: 'border-box',
  },
  removeBtn: {
    border: 'none',
    background: 'var(--surface-sunken)',
    borderRadius: 6,
    width: 22,
    height: 22,
    cursor: 'pointer',
    fontSize: 10,
    color: 'var(--fg-4)',
  },
};
